import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getRegionBySlug, getCitiesForRegion, REGIONS } from "../lib/data/regions";
import { SERVICES } from "../lib/data/services";
import { setSEO } from "../lib/data/seo";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import EditorialMarquee from "../components/site/EditorialMarquee";
import ContactBlock from "../components/site/ContactBlock";

export default function RegionDetail() {
  const { slug } = useParams();
  const region = getRegionBySlug(slug);

  useEffect(() => {
    if (!region) return;
    setSEO({
      title: `${region.name} Security Services — Regional BC Hub | Maze Security Solutions`,
      description: `${region.tagline} Licensed security across the ${region.name} region including all cities in the area. Call for coverage.`,
      keywords: region.keywords,
      canonical: `${window.location.origin}/regions/${region.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${region.name} Security Services`,
        areaServed: `${region.name}, British Columbia`,
        provider: {
          "@type": "SecurityService",
          name: "Maze Security Solutions",
          areaServed: "British Columbia, Canada",
        },
        description: region.overview,
      },
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [region]);

  if (!region) return <Navigate to="/regions" replace />;

  const cities = getCitiesForRegion(region.name);
  const otherRegions = REGIONS.filter((r) => r.slug !== region.slug).slice(0, 6);

  return (
    <div className="pt-24">
      <section className="pt-12 md:pt-20 pb-16 bg-[#F4F4F0]" data-testid="region-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <Link to="/regions" className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              ← All Regions
            </Link>
            <RevealLines
              lines={region.name.split(" ")}
              className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
            />
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
              {region.tagline}
            </p>
          </div>
          <div className="md:col-span-5">
            <SpotlightImage
              src={region.image}
              alt={region.name}
              caption={region.name}
              className="aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <EditorialMarquee items={[region.name.toUpperCase(), "BRITISH COLUMBIA", "LICENSED · BONDED · INSURED", "24 / 7 DISPATCH"]} />

      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="region-overview">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Overview
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[0.95] text-[#060913]">
              What {region.name} actually needs.
            </h2>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="text-lg md:text-xl text-[#060913] leading-relaxed">
                {region.overview}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-black/10 py-24" data-testid="region-industries">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Industries served
            </div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl tracking-tight text-[#060913]">
              Local focus, provincial standards.
            </h3>
          </div>
          <div className="md:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-black/10">
              {region.industries.map((ind, i) => (
                <li key={ind} className="p-6 border-b border-r border-black/10 last:border-r-0">
                  <div className="font-mono text-[10px] tracking-[0.22em] text-[#D92323]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-2 text-xl text-[#060913]">{ind}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {cities.length > 0 && (
        <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="region-cities">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Cities in {region.name}
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[#060913] max-w-4xl">
              {cities.length} {cities.length === 1 ? "city" : "cities"} under one regional dispatch.
            </h2>
            <div className="mt-14 border-t border-black/10">
              {cities.map((c, i) => (
                <Link
                  key={c.slug}
                  to={`/cities/${c.slug}`}
                  data-testid={`region-city-${c.slug}`}
                  className="group grid grid-cols-12 gap-4 items-baseline py-6 border-b border-black/10 hover:bg-white transition-colors"
                >
                  <span className="col-span-1 font-mono text-[11px] tracking-[0.22em] text-black/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-7 md:col-span-6 font-display text-2xl md:text-4xl leading-tight text-[#060913] group-hover:text-[#D92323] transition-colors">
                    {c.name}
                  </span>
                  <span className="hidden md:block col-span-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#5C5F66]">
                    {c.population ? `Population · ${c.population}` : "Coverage · 24/7"}
                  </span>
                  <span className="col-span-4 md:col-span-1 text-right font-mono text-[11px] tracking-[0.22em] uppercase text-black/50 group-hover:text-[#D92323]">
                    →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services in the region */}
      <section className="bg-white border-y border-black/10 py-24" data-testid="region-services">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Services in {region.name}
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight text-[#060913] max-w-3xl">
            Every discipline, deployed locally.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#060913]/70 hover:text-[#D92323] border-b border-black/10 hover:border-[#D92323] py-2"
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Other regions */}
      <section className="bg-[#F4F4F0] py-20" data-testid="region-related">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323] mb-6">
            Other BC regions
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {otherRegions.map((r) => (
              <Link
                key={r.slug}
                to={`/regions/${r.slug}`}
                className="font-display text-xl md:text-2xl text-[#060913] hover:text-[#D92323] border-b border-black/10 py-3"
              >
                {r.name} →
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#060913] text-white py-24 md:py-32" data-testid="region-contact">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Next Step · {region.name}
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Coverage across {region.name}, one call.
            </h2>
          </div>
          <div className="md:col-span-6">
            <ContactBlock variant="dark" testid={`region-${region.slug}-contact`} />
          </div>
        </div>
      </section>
    </div>
  );
}
